import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import SearchService from '../../services/search.services';
import { useCompanyId } from '../../services/CompanyidProvider';
import { FluxButton } from '@meltwater/flux-web-components-react';
import { Stack } from '@mui/material';
import styles from './search.module.css';
import { getJwtToken } from '../../services/jwt.services';
import jwt_decode from 'jwt-decode';
import { useSearchData } from './searchexports';
import DataTable from '../../components/DataTable';

const DeletedSearch = ({ load, setActiveTab }) => {
    const [deletedSearchData, setDeletedSearchdata] = useState(null);
    const [permissions, setPermssions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [openSnackBar, setOpenSnackBar] = useState(false);
    const [feedbackMsg, setFeedbackMsg] = useState('');
    const [severity, setSeverity] = useState('info');
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadingMessage, setLoadingMessage] = useState('Loading deleted searches...');
    const { deletedSearchData: cachedDeletedData, setDeletedSearchData: setCachedDeletedData } = useSearchData();
    const { companyId } = useCompanyId();
    const loadingTimerRef = useRef(null);

    // Custom guide steps specific to DeletedSearch
    const deletedSearchGuideSteps = useMemo(() => [
        {
            title: 'Deleted Searches Management',
            text: 'This guide will help you navigate the Deleted Searches recovery features.',
            element: null,
        },
        {
            title: 'Search Deleted Items',
            text: 'Use the search box to quickly find deleted searches by name or ID.',
            element: '.mrt-input',
            position: { top: 20, left: 200 }
        },
        {
            title: 'Column Visibility & Table Settings',
            text: 'Use these buttons to show or hide specific columns and adjust table settings.',
            element: '.mrt-button.mrt-dropdown-button',
            position: { top: 0, right: 80 }
        },
        {
            title: 'Restore Selected',
            text: 'Use this button to restore selected searches that were previously deleted.',
            element: '.MuiButtonBase-root.FluxButton-root',
            position: { top: 0, left: 100 }
        },
        {
            title: 'Restore Information',
            text: 'Restored searches will become available again in the Searches section. This operation cannot be undone.',
            element: null,
        },
        {
            title: 'Refresh Data',
            text: 'Use this button to refresh the deleted searches list from the server.',
            element: '.MuiButtonBase-root.FluxButton-root:nth-of-type(2)',
            position: { top: 0, left: 300 }
        },
        {
            title: 'Search Selection',
            text: 'Select searches with checkboxes to perform operations on specific deleted searches.',
            element: '.mrt-row-select-checkbox',
            position: { top: 100, left: 30 }
        },
        {
            title: 'Deletion Date',
            text: 'The deletedAt column shows when each search was deleted from the system.',
            element: '.mrt-cell:nth-of-type(3)',
            position: { top: 100, left: 200 }
        }
    ], []);

    const columns = useMemo(
        () => [
            {
                header: 'Name',
                accessorKey: 'name',
                enableColumnFilterModes: false,
                filterFn: 'contains',
            },
            {
                header: 'id',
                accessorKey: 'savedSearchId',
            },
            {
                header: 'deletedAt',
                accessorKey: 'deletedAt',
            }
        ],
        [],
    );
    
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        setLoadingMessage('Fetching deleted searches...');
        loadingTimerRef.current = setTimeout(() => {
            setLoadingMessage('Please wait, it takes longer than usual for larger companies...');
        }, 15000);
        
        try {
            const myres = await SearchService.getDeletedSearches(companyId);
            setDeletedSearchdata(myres.data);
            setCachedDeletedData(myres.data);
            
            setFeedbackMsg(`Successfully loaded ${myres.data.length} deleted searches.`);
            setSeverity('success');
            setOpenSnackBar(true);
            setHasError(false);
        } catch (error) {
            console.log(`FAIL`, error);
            const errorMsg = error.message || 'Unknown error';
            setErrorMessage(errorMsg);
            setFeedbackMsg(`Error loading deleted searches: ${errorMsg}`);
            setSeverity('error');
            setOpenSnackBar(true);
            setHasError(true);
        } finally {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
            setLoadingMessage('');
            setIsLoading(false);
        }
    }, [companyId, setCachedDeletedData, setFeedbackMsg, setSeverity, setOpenSnackBar, setHasError, setErrorMessage]);
    
    useEffect(() => {
        async function loadData() {
            if (cachedDeletedData) {
                setDeletedSearchdata(cachedDeletedData);
            } else {
                await fetchData();
            }
        }
        
        if (load) {
            loadData();
        }
        
        return () => {
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
                loadingTimerRef.current = null;
            }
        };
    }, [load, cachedDeletedData, companyId, fetchData]);

    const handleCloseSnackBar = () => {
        setOpenSnackBar(false);
    };
    
    const refreshData = async () => {
        try {
            setIsLoading(true);
            setCachedDeletedData(null);
            await fetchData();
            setFeedbackMsg('Data refreshed successfully');
            setSeverity('success');
        } catch (err) {
            console.error('Error refreshing data:', err);
            setFeedbackMsg(`Error refreshing data: ${err.message}`);
            setSeverity('error');
        } finally {
            setIsLoading(false);
            setOpenSnackBar(true);
        }
    };

    const handleRestoreSearches = async (rows) => {
        try {
            setIsLoading(true);
            const response = await SearchService.restoreDeletedSearches(
                companyId, 
                rows.map(row => row.original.savedSearchId.toString())
            );
            
            if (response.data.searchIdsRestored.length > 0) {
                setFeedbackMsg(`Successfully restored ${response.data.searchIdsRestored.length} searches.`);
                setSeverity('success');
                await refreshData();
            }
            
            if (response.data.searchIdsRestored.length !== rows.length) {
                throw new Error(`Some searches were not restored. ${JSON.stringify({...response.data})}. Please create a TRITON ticket`);
            }
        } catch (error) {
            console.error('Error restoring searches:', error);
            setFeedbackMsg(`Error: ${error.message}`);
            setSeverity('error');
            setOpenSnackBar(true);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        const jwtToken = getJwtToken();

        if (jwtToken) {
            const { permissions } = jwt_decode(jwtToken);
            setPermssions(permissions);
        }
    }, []);
    
    if (!permissions || !permissions.includes('read')) {
        // If user does not have permission, you can render a message, redirect, or display a different component
        return <div>You do not have permission to access this page.</div>;
    }
    
    // Custom toolbar actions for the DataTable
    const renderToolbarActions = ({ table }) => {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                {permissions.includes("write") && 
                    <FluxButton
                        style={{ margin: 2 }}
                        tint="primary"
                        type="filled"
                        onClick={() => handleRestoreSearches(table.getSelectedRowModel().rows)}
                        disabled={!table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()}
                    >
                        Restore Selected
                    </FluxButton>
                }
                <FluxButton
                    style={{ margin: 2 }}
                    tint="secondary"
                    type="filled"
                    onClick={refreshData}
                >
                    Refresh Data
                </FluxButton>
            </Stack>
        );
    };

    return (
        <div className={styles.Search}>
            <DataTable 
                columns={columns}
                data={deletedSearchData || []}
                isLoading={isLoading && !hasError}
                loadingMessage={loadingMessage || 'Loading deleted searches...'}
                hasError={hasError}
                errorMessage={errorMessage}
                errorTitle="Error Loading Deleted Searches"
                onBackClick={() => setActiveTab(null)}
                renderToolbarActions={renderToolbarActions}
                backTooltip="Return to Dashboard"
                showGuide={false}
                guideSteps={deletedSearchGuideSteps}
                snackbarProps={{
                    open: openSnackBar,
                    onClose: handleCloseSnackBar,
                    message: feedbackMsg,
                    severity: severity,
                    autoHideDuration: 10000
                }}
                tableProps={{
                    muiSearchTextFieldProps: {
                        placeholder: 'Search deleted items...',
                        variant: 'standard',
                        sx: { width: '300px' }
                    },
                    initialState: {
                        density: 'compact',
                        pagination: { pageSize: 100 },
                    }
                }}
            />
        </div>
    );
};

export default DeletedSearch;